import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, Sparkle, Crown, Gift, Copy, ShareNetwork, WhatsappLogo, EnvelopeSimple, XLogo, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, ensureUserId } from "@/lib/api";

export default function Subscription() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/subscriptions").then((r) => setPlans(r.data));
    const uid = ensureUserId();
    api.get(`/referral/${uid}`).then((r) => setReferral(r.data));
  }, []);

  const log = (channel) => api.post("/referral/share", { user_id: ensureUserId(), channel }).catch(() => {});

  const copyLink = async () => {
    if (!referral) return;
    try {
      await navigator.clipboard.writeText(referral.share_url);
      setCopied(true);
      log("link");
      toast.success("Invite link copied", { description: "Share it with anyone who wants to learn." });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy manually.");
    }
  };

  const nativeShare = async () => {
    if (!referral) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "SuperLearn AI", text: referral.share_message, url: referral.share_url });
        log("message");
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  const shareTo = (channel) => {
    if (!referral) return;
    const msg = encodeURIComponent(`${referral.share_message} ${referral.share_url}`);
    const urls = {
      whatsapp: `https://wa.me/?text=${msg}`,
      x: `https://twitter.com/intent/tweet?text=${msg}`,
      email: `mailto:?subject=${encodeURIComponent("SuperLearn AI invite")}&body=${msg}`,
    };
    log(channel);
    window.open(urls[channel], "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen mesh-bg pb-20" data-testid="membership-screen">
      <div className="px-6 pt-12 flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="sub-back" className="w-9 h-9 rounded-full glass flex items-center justify-center active:scale-95">
          <ArrowLeft size={16} />
        </button>
        <div>
          <p className="text-[10px] tracking-widest uppercase text-zinc-500">Membership</p>
          <h1 className="font-display text-3xl font-light tracking-tight">One pass. Every mentor.</h1>
        </div>
      </div>

      {/* Invite a Learner */}
      <div className="mt-6 px-6">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative glass-strong rounded-3xl p-6 overflow-hidden"
          data-testid="invite-card"
        >
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-emerald-400/15 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-indigo-400/10 blur-3xl" />

          <div className="relative flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-emerald-400/15 flex items-center justify-center shrink-0">
              <Gift size={20} weight="fill" className="text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">Invite a learner</div>
              <h3 className="font-display text-xl font-light leading-tight mt-1">Give a month free, get a month free.</h3>
              <p className="text-sm text-zinc-400 mt-1">{referral?.perk || "Both you and your friend earn 1 month of Operator."}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="relative mt-5 grid grid-cols-3 gap-2">
            <Pill label="Invited" value={referral?.invited_count ?? 0} testid="invited-count" />
            <Pill label="XP / invite" value={`+${referral?.xp_per_invite ?? 250}`} accent="#FBBF24" testid="xp-per-invite" />
            <Pill label="XP earned" value={referral?.xp_earned ?? 0} accent="#34D399" testid="xp-earned" />
          </div>

          {/* Code + copy */}
          <div className="relative mt-5 flex items-center gap-2 glass rounded-full px-4 py-3">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Code</span>
            <span className="font-mono-jb text-sm flex-1 truncate" data-testid="referral-code">{referral?.code || "—"}</span>
            <button
              onClick={copyLink}
              data-testid="copy-link-btn"
              className="rounded-full bg-white text-black text-xs font-medium px-3 py-1.5 flex items-center gap-1 active:scale-95 transition"
            >
              {copied ? <><CheckCircle size={12} weight="fill" /> Copied</> : <><Copy size={12} weight="bold" /> Copy link</>}
            </button>
          </div>

          {/* Share */}
          <div className="relative mt-3 grid grid-cols-4 gap-2">
            <ShareBtn icon={<ShareNetwork size={16} weight="fill" />} label="Share" onClick={nativeShare} testid="share-native" />
            <ShareBtn icon={<WhatsappLogo size={16} weight="fill" />} label="WhatsApp" onClick={() => shareTo("whatsapp")} testid="share-whatsapp" />
            <ShareBtn icon={<XLogo size={14} weight="fill" />} label="X" onClick={() => shareTo("x")} testid="share-x" />
            <ShareBtn icon={<EnvelopeSimple size={16} weight="fill" />} label="Email" onClick={() => shareTo("email")} testid="share-email" />
          </div>
        </motion.div>
      </div>

      {/* Plans */}
      <div className="mt-6 px-6 space-y-4">
        {plans.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            className={`relative rounded-3xl p-6 overflow-hidden ${p.highlight ? "glass-strong border border-amber-300/30" : "glass"}`}
            data-testid={`plan-${p.id}`}
          >
            {p.highlight && (
              <>
                <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-amber-300/15 blur-3xl" />
                <div className="absolute top-0 left-0 right-0 h-px shimmer-line bg-gradient-to-r from-transparent via-amber-200/40 to-transparent" />
              </>
            )}

            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  {p.id === "founder" && <Crown size={14} weight="fill" className="text-amber-300" />}
                  {p.id === "pro" && <Sparkle size={14} weight="fill" className="text-violet-300" />}
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">{p.name}</span>
                </div>
                <div className="mt-1 text-zinc-400 text-sm">{p.tagline}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-4xl font-light">${p.price}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">/{p.period}</div>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              {p.features.map((f) => (
                <div key={f} className="flex items-start gap-2 text-sm text-zinc-200">
                  <Check size={14} weight="bold" className="text-emerald-400 mt-1 shrink-0" />
                  <span>{f}</span>
                </div>
              ))}
            </div>

            <button
              data-testid={`subscribe-${p.id}`}
              className={`mt-5 w-full rounded-full py-3.5 text-sm font-medium transition active:scale-[0.98] ${
                p.highlight ? "bg-white text-black" : "glass-strong text-white"
              }`}
            >
              {p.price === 0 ? "Continue free" : `Choose ${p.name}`}
            </button>
          </motion.div>
        ))}

        <div className="pt-4 text-[11px] text-zinc-500 text-center leading-relaxed">
          Enterprise learning · Cohorts for teams ·
          <button className="text-zinc-300 underline ml-1" data-testid="enterprise-link">Talk to us</button>
        </div>
      </div>
    </div>
  );
}

const Pill = ({ label, value, accent, testid }) => (
  <div className="rounded-2xl bg-white/[0.04] p-3 text-center relative overflow-hidden" data-testid={testid}>
    {accent && <div className="absolute -bottom-4 -right-4 w-12 h-12 rounded-full blur-lg" style={{ background: accent, opacity: 0.18 }} />}
    <div className="font-display text-xl font-light" style={{ color: accent || "#fff" }}>{value}</div>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">{label}</div>
  </div>
);

const ShareBtn = ({ icon, label, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="glass rounded-2xl py-3 flex flex-col items-center gap-1 text-zinc-200 active:scale-95 transition"
  >
    {icon}
    <span className="text-[10px] tracking-wide">{label}</span>
  </button>
);
